/**
 * TaskDetailPanel
 * Inline task editor for create/edit in split view
 */

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateTask, useUpdateTask, useDeleteTask, useCompleteTask } from '@/hooks/use-tasks';
import { useToast } from '@/hooks/use-toast';
import {
  TASK_PRIORITY_VALUES,
  PRIORITY_FROM_VALUE,
  type TaskType,
  type TaskPriority,
  type UserTask,
} from '@/types/task';
import { LEGAL_SUBJECTS } from '@/types/learning-profile';
import { Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface TaskDetailPanelProps {
  task?: UserTask | null; // If provided, edit mode; otherwise create mode
  onSave?: () => void;
  onCancel?: () => void;
}

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const TASK_TYPE_OPTIONS: { value: TaskType; label: string }[] = [
  { value: 'study_session', label: 'Study Session' },
  { value: 'reading', label: 'Reading Assignment' },
  { value: 'outline', label: 'Build/Review Outline' },
  { value: 'essay', label: 'Essay Practice' },
  { value: 'flashcards', label: 'Flashcard Review' },
  { value: 'practice', label: 'Practice Questions' },
  { value: 'review_topic', label: 'Review Topic' },
  { value: 'diagnostic', label: 'Diagnostic Assessment' },
  { value: 'other', label: 'Other' },
];

export function TaskDetailPanel({ task, onSave, onCancel }: TaskDetailPanelProps) {
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const completeTask = useCompleteTask();
  const { toast } = useToast();
  const isEditMode = !!task;

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [taskType, setTaskType] = useState<TaskType>('study_session');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [subject, setSubject] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState('');

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Initialize form with task data in edit mode
  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setTaskType(task.task_type);
      setPriority(PRIORITY_FROM_VALUE[task.priority] || 'normal');
      // Parse date and time from ISO string
      if (task.due_date) {
        const [datePart, timePart] = task.due_date.split('T');
        setDueDate(datePart);
        // Extract time if present (format: HH:MM:SS or HH:MM)
        if (timePart) {
          setDueTime(timePart.substring(0, 5)); // Get HH:MM
        } else {
          setDueTime('');
        }
      } else {
        setDueDate('');
        setDueTime('');
      }
      setSubject(task.related_subject || '');
      setEstimatedMinutes(task.estimated_minutes?.toString() || '');
    } else {
      resetForm();
    }
  }, [task]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTaskType('study_session');
    setPriority('normal');
    setDueDate('');
    setDueTime('');
    setSubject('');
    setEstimatedMinutes('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) return;

    // Combine date and time into ISO string
    let dueDateTimeValue: string | undefined;
    if (dueDate) {
      if (dueTime) {
        // Combine date and time
        dueDateTimeValue = `${dueDate}T${dueTime}:00`;
      } else {
        // Date only - store as date string
        dueDateTimeValue = dueDate;
      }
    }

    try {
      if (isEditMode && task) {
        await updateTask.mutateAsync({
          taskId: task.id,
          updates: {
            title: title.trim(),
            description: description.trim() || undefined,
            task_type: taskType,
            priority: TASK_PRIORITY_VALUES[priority],
            due_date: dueDateTimeValue,
            estimated_minutes: estimatedMinutes ? parseInt(estimatedMinutes, 10) : undefined,
            related_subject: subject || undefined,
          },
        });

        toast({
          title: 'Task updated',
          description: 'Your changes have been saved.',
        });
      } else {
        await createTask.mutateAsync({
          title: title.trim(),
          description: description.trim() || undefined,
          task_type: taskType,
          priority: TASK_PRIORITY_VALUES[priority],
          due_date: dueDateTimeValue,
          estimated_minutes: estimatedMinutes ? parseInt(estimatedMinutes, 10) : undefined,
          related_subject: subject || undefined,
          source: 'user_created',
        });

        toast({
          title: 'Task created',
          description: 'Your task has been added to the list.',
        });
      }

      onSave?.();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} task:`, error);
      toast({
        title: 'Error',
        description: `Failed to ${isEditMode ? 'update' : 'create'} task. Please try again.`,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!task) return;

    try {
      await deleteTask.mutateAsync(task.id);
      toast({
        title: 'Task deleted',
        description: 'The task has been removed.',
      });
      onSave?.();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete task. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleComplete = async () => {
    if (!task) return;

    try {
      await completeTask.mutateAsync({ taskId: task.id });
      toast({
        title: 'Task completed',
        description: 'Great job! Task marked as complete.',
      });
      onSave?.();
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete task. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const isPending = createTask.isPending || updateTask.isPending || deleteTask.isPending || completeTask.isPending;

  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Review Contracts hearsay exceptions"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional details about this task..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            {/* Task Type & Priority Row */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="taskType">Task Type</Label>
                <Select value={taskType} onValueChange={(v) => setTaskType(v as TaskType)}>
                  <SelectTrigger id="taskType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Select value={subject || '_none'} onValueChange={(v) => setSubject(v === '_none' ? '' : v)}>
                <SelectTrigger id="subject">
                  <SelectValue placeholder="Select subject (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {LEGAL_SUBJECTS.map((subj) => (
                    <SelectItem key={subj.value} value={subj.value}>
                      {subj.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Due Date, Time & Estimated Time Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date</Label>
                <div className="relative">
                  <Input
                    id="dueDate"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    title=""
                    className={cn(
                      !dueDate && '[&::-webkit-datetime-edit]:opacity-0'
                    )}
                  />
                  {!dueDate && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none select-none text-sm">
                      Select date...
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueTime">Due Time</Label>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={cn('relative', !dueDate && 'cursor-not-allowed')}>
                        <Input
                          id="dueTime"
                          type="time"
                          value={dueTime}
                          onChange={(e) => setDueTime(e.target.value)}
                          disabled={!dueDate}
                          title=""
                          className={cn(!dueDate && 'pointer-events-none')}
                        />
                      </div>
                    </TooltipTrigger>
                    {!dueDate && (
                      <TooltipContent>
                        <p>Select a due date first</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedMinutes">Est. (min)</Label>
                <Input
                  id="estimatedMinutes"
                  type="number"
                  min="1"
                  placeholder="30"
                  value={estimatedMinutes}
                  onChange={(e) => setEstimatedMinutes(e.target.value)}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t">
              {/* Left side - destructive actions (edit mode only) */}
              {isEditMode && task?.status !== 'completed' && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isPending}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleComplete}
                    disabled={isPending}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                  >
                    {completeTask.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    )}
                    Complete
                  </Button>
                </div>
              )}

              {/* Right side - primary actions */}
              <div className="flex gap-2 justify-end sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!title.trim() || isPending}>
                  {(createTask.isPending || updateTask.isPending) ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isEditMode ? 'Saving...' : 'Creating...'}
                    </>
                  ) : (
                    isEditMode ? 'Save Changes' : 'Create Task'
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default TaskDetailPanel;
